import * as echarts from "echarts/core";
import { getChartColor, AXIS_TEXT_COLOR, DIMMED_OPACITY, } from "../utils/chartConstants";
import { formatNumericValue, getChartDataset, parseNumericValue, resolveLabelAndValueKeys, } from "../../../utils/chartData";
import { buildBarValueLabel, getPaddedAxisMax } from "../utils/barValueLabel";
export function buildSimpleBarOption(data, baseType, isHorizontalBar, isStackedBar, isLabeledBar, isCleanBarVariant, selectedSeries, useSimpleBarLegendFilter, isMobile) {
    var dataset = getChartDataset(data);
    var _a = resolveLabelAndValueKeys(data, dataset), labelKey = _a.labelKey, valueKey = _a.valueKey;
    var labels = dataset.map(function (item) { return item[labelKey]; });
    var values = dataset.map(function (item) { var _a; return parseNumericValue((_a = item[valueKey]) !== null && _a !== void 0 ? _a : item.value); });
    var filteredLabels = useSimpleBarLegendFilter && selectedSeries.length > 0
        ? labels.filter(function (label) { return selectedSeries.includes(label); })
        : labels;
    var filteredValues = useSimpleBarLegendFilter && selectedSeries.length > 0
        ? values.filter(function (_, index) { return selectedSeries.includes(labels[index]); })
        : values;
    var shouldRotateSimpleLabels = (!useSimpleBarLegendFilter && labels.length > 6) ||
        (!useSimpleBarLegendFilter &&
            labels.some(function (label) { return label != null && String(label).length > 10; }));
    var simpleBarSeries = [
        {
            type: "bar",
            stack: isStackedBar ? "total" : undefined,
            label: buildBarValueLabel(isHorizontalBar ? "right" : "top"),
            data: filteredValues.map(function (v, i) {
                var _a;
                var color = getChartColor(filteredLabels[i], useSimpleBarLegendFilter && selectedSeries.length > 0
                    ? labels.indexOf(filteredLabels[i])
                    : i);
                return {
                    value: v,
                    itemStyle: {
                        color: ((_a = echarts.graphic) === null || _a === void 0 ? void 0 : _a.LinearGradient)
                            ? new echarts.graphic.LinearGradient(isHorizontalBar ? 1 : 0, 0, 0, isHorizontalBar ? 0 : 1, [
                                { offset: 0, color: color },
                                { offset: 1, color: color + "AA" },
                            ])
                            : color,
                        borderRadius: isHorizontalBar ? [0, 6, 6, 0] : [6, 6, 0, 0],
                        borderColor: "#FFFFFF",
                        borderWidth: isStackedBar ? 1 : 0,
                        opacity: selectedSeries.length === 0 ||
                            selectedSeries.includes(filteredLabels[i])
                            ? 1
                            : DIMMED_OPACITY,
                    },
                };
            }),
            barWidth: "50%",
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowColor: "rgba(0,0,0,0.12)",
                },
            },
        },
    ];
    return {
        tooltip: {
            trigger: "axis",
            backgroundColor: "rgba(255,255,255,0.96)",
            borderColor: "#ECEEF4",
            borderWidth: 1,
            textStyle: {
                fontFamily: "Montserrat, sans-serif",
                color: "#374151",
                fontSize: 13,
            },
            extraCssText: "box-shadow: 0 4px 20px rgba(0,0,0,0.08); border-radius: 10px;",
            formatter: function (params) {
                var _a, _b, _c, _d, _e;
                if (Array.isArray(params)) {
                    var axisLabel = (_d = (_b = (_a = params[0]) === null || _a === void 0 ? void 0 : _a.axisValueLabel) !== null && _b !== void 0 ? _b : (_c = params[0]) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : "";
                    if (params.length === 1) {
                        return "<strong>".concat(axisLabel, "</strong><br/><strong>").concat(formatNumericValue((_e = params[0]) === null || _e === void 0 ? void 0 : _e.value), "</strong>");
                    }
                    var rows = params.map(function (item) {
                        var _a, _b, _c;
                        var marker = (_a = item.marker) !== null && _a !== void 0 ? _a : "";
                        var seriesName = (_c = (_b = item.seriesName) !== null && _b !== void 0 ? _b : item.name) !== null && _c !== void 0 ? _c : "";
                        return "".concat(marker).concat(seriesName, ": <strong>").concat(formatNumericValue(item.value), "</strong>");
                    });
                    return "<strong>".concat(axisLabel, "</strong><br/>").concat(rows.join("<br/>"));
                }
                return "<strong>".concat(params.name, "</strong><br/><strong>").concat(formatNumericValue(params.value), "</strong>");
            },
        },
        grid: {
            left: isMobile ? "2%" : "1%",
            right: isMobile ? "3%" : "2%",
            bottom: useSimpleBarLegendFilter
                ? isMobile ? "4%" : "3%"
                : shouldRotateSimpleLabels
                    ? isMobile ? "18%" : "16%"
                    : "3%",
            top: isHorizontalBar ? "10%" : "14%",
            containLabel: true,
        },
        xAxis: isHorizontalBar
            ? {
                type: "value",
                max: getPaddedAxisMax(filteredValues),
                axisLabel: {
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: 11,
                    color: AXIS_TEXT_COLOR,
                    formatter: function (value) { return formatNumericValue(value); },
                },
                splitLine: isCleanBarVariant
                    ? { show: false }
                    : { lineStyle: { color: "#F3F4F6", type: "dashed" } },
            }
            : {
                type: "category",
                data: filteredLabels,
                axisLabel: {
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: isMobile ? 10 : 11,
                    color: AXIS_TEXT_COLOR,
                    interval: 0,
                    show: !useSimpleBarLegendFilter,
                    rotate: shouldRotateSimpleLabels ? (isMobile ? 28 : 34) : 0,
                },
                axisLine: { lineStyle: { color: "#E5E7EB" } },
                axisTick: { show: false },
            },
        yAxis: isHorizontalBar
            ? {
                type: "category",
                data: filteredLabels,
                axisLabel: {
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: isMobile ? 10 : 11,
                    color: AXIS_TEXT_COLOR,
                    interval: 0,
                },
                axisLine: { lineStyle: { color: "#E5E7EB" } },
                axisTick: { show: false },
            }
            : {
                type: "value",
                max: getPaddedAxisMax(filteredValues),
                axisLabel: {
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: 11,
                    color: AXIS_TEXT_COLOR,
                    formatter: function (value) { return formatNumericValue(value); },
                },
                splitLine: isCleanBarVariant
                    ? { show: false }
                    : { lineStyle: { color: "#F3F4F6", type: "dashed" } },
            },
        series: simpleBarSeries,
    };
}
